import type { D1Database } from "@cloudflare/workers-types";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { bindings } from "../bindings.server";
import { normalizeFacilities as normalizeFacilityList } from "../facilities";
import { coverSources } from "../place-photos";
import { MIN_CLUBS_PER_THEME_PAGE } from "../themes";
import { matchesSearch, rankSearch } from "../search-text";
import { haversineKm } from "../geo";

export type BeachLayout = {
  unit: string;
  price: number;
  currency: string;
  rows: { id: string; spots: number }[];
};

export type Club = {
  id: number;
  slug: string;
  name: string;
  country: string;
  region_key: string;
  region_label: string;
  town: string;
  lat: number | null;
  lng: number | null;
  approx_location: number;
  description: string;
  price_from: number | null;
  price_unit: string | null;
  currency: string;
  amenities: string[];
  whatsapp: string | null;
  instagram: string | null;
  website: string | null;
  email: string | null;
  image: string | null;
  // Credit for `image`, only filled when its licence requires attribution
  // (CC BY and the like). NULL for a club's own photo and for Pexels/Unsplash.
  image_credit: string | null;
  image_credit_url: string | null;
  verified: number;
  featured: number;
  sponsored: number;
  phone: string | null;
  layout: BeachLayout | null;
  researched_at: string | null;
  season_note: string | null;
  avg_rating: number | null;
  review_count: number;
  long_description: string | null;
  opening_hours: string | null;
  rules: string | null;
  facilities: string[];
  best_for: string | null;
  google_place_id: string | null;
  google_rating: number | null;
  google_review_count: number | null;
  google_synced_at: string | null;
  // The first Google Places photo for this club, as a photo resource name.
  // Rendered through /api/place-photo, and always beaten by `image` when the
  // club has uploaded a picture of its own.
  google_photo: string | null;
  google_photo_attribution: string | null;
};

type ClubRow = Omit<Club, "amenities" | "layout" | "facilities"> & {
  amenities: string;
  layout: string | null;
  facilities: string | null;
};

function parseClub(row: ClubRow): Club {
  let amenities: string[] = [];
  try {
    const parsed = JSON.parse(row.amenities);
    if (Array.isArray(parsed)) amenities = parsed.filter((a) => typeof a === "string");
  } catch {
    amenities = [];
  }
  let layout: BeachLayout | null = null;
  if (row.layout) {
    try {
      const parsed = JSON.parse(row.layout);
      if (parsed && Array.isArray(parsed.rows)) layout = parsed as BeachLayout;
    } catch {
      layout = null;
    }
  }
  let facilities: string[] = [];
  if (row.facilities) {
    try {
      const parsed = JSON.parse(row.facilities);
      if (Array.isArray(parsed)) facilities = parsed.filter((f) => typeof f === "string");
    } catch {
      facilities = [];
    }
  }
  return { ...row, amenities, layout, facilities };
}

// Correlated subqueries, not a JOIN + GROUP BY: keeps every other column a
// plain, unambiguous `c.*` and stays cheap at this catalog size (a few
// hundred clubs, a few thousand reviews at most for the foreseeable future).
const CLUB_SELECT = `SELECT c.*,
    (SELECT ROUND(AVG(rating), 1) FROM reviews r WHERE r.club_id = c.id AND r.status = 'published') AS avg_rating,
    (SELECT COUNT(*) FROM reviews r WHERE r.club_id = c.id AND r.status = 'published') AS review_count,
    (SELECT p.photo_name FROM place_photos p WHERE p.club_slug = c.slug ORDER BY p.sort LIMIT 1) AS google_photo,
    (SELECT p.attribution FROM place_photos p WHERE p.club_slug = c.slug ORDER BY p.sort LIMIT 1) AS google_photo_attribution
  FROM clubs c`;

// The listing pages render cards, not club pages, so they have no use for the
// long write-up, the house rules, the opening hours or the beach layout. Those
// four fields are most of the weight of a club row, and shipping them for 340
// clubs put well over a megabyte of prose into the HTML of /clubs. They are
// selected as NULL so the row still satisfies the Club type and the card
// components need no separate shape.
const LIST_SELECT = `SELECT c.id, c.slug, c.name, c.country, c.region_key, c.region_label,
    c.town, c.lat, c.lng, c.approx_location, '' AS description, c.price_from, c.price_unit,
    c.currency, c.amenities, c.facilities, c.whatsapp, c.instagram, c.website, c.email,
    c.image, c.image_credit, c.image_credit_url,
    c.verified, c.featured, c.sponsored, c.phone, c.researched_at, c.season_note,
    c.best_for, c.google_place_id, c.google_rating, c.google_review_count, c.google_synced_at,
    NULL AS long_description, NULL AS opening_hours, NULL AS rules, NULL AS layout,
    -- The one-line summary is not rendered on any card either, so it is blanked
    -- rather than shipped 340 times.
    (SELECT ROUND(AVG(rating), 1) FROM reviews r WHERE r.club_id = c.id AND r.status = 'published') AS avg_rating,
    (SELECT COUNT(*) FROM reviews r WHERE r.club_id = c.id AND r.status = 'published') AS review_count,
    (SELECT p.photo_name FROM place_photos p WHERE p.club_slug = c.slug ORDER BY p.sort LIMIT 1) AS google_photo,
    (SELECT p.attribution FROM place_photos p WHERE p.club_slug = c.slug ORDER BY p.sort LIMIT 1) AS google_photo_attribution
  FROM clubs c`;

export const listClubs = createServerFn({ method: "GET" }).handler(async () => {
  const { DB } = bindings();
  if (!DB) return { clubs: [] as Club[] };
  const { results } = await DB.prepare(
    `${LIST_SELECT} WHERE c.hidden = 0 ORDER BY c.sponsored DESC, c.verified DESC, c.featured DESC, c.name ASC`,
  ).all<ClubRow>();
  return { clubs: (results ?? []).map(parseClub) };
});

export type ClubSearch = {
  q?: string;
  region?: string;
  facilities?: string[];
  priced?: boolean;
  page?: number;
};

export type SearchResult = {
  clubs: Club[];
  total: number;
  page: number;
  pages: number;
  /** Facility facets for the current region, with counts, most common first. */
  facets: [string, number][];
  pricedCount: number;
  /**
   * Every region that has clubs, with its country and count. Counted over the
   * whole catalogue rather than the current result set, so the navigation
   * never shrinks as you filter: a list of places to go should not hide the
   * place you were about to click.
   */
  regions: { key: string; label: string; country: string; count: number }[];
};

export const PAGE_SIZE = 24;

// Server-side search, paging and facets.
//
// The catalogue used to ship every club to the browser and filter there, which
// was fine at 210 rows and became 3 MB of HTML and 826 image tags at 827. On a
// phone that is the whole page budget spent before anyone has scrolled.
//
// Two queries: a narrow index over every visible club to filter and count
// against, then the full rows for one page. The index stays in the worker and
// never reaches the browser.
// `regionKey` and `country` travel with every kind, not just the region one:
// the database stores "Sicily" and "Italy", and a Dutch page has to be able to
// render "Sicilie" and "Italie". Sending the display string ready-made would
// hard-code English into the suggestion list.
export type ClubSocial = { network: string; handle: string; url: string };

export type Suggestion =
  | { kind: "region"; label: string; regionKey: string; country: string; count: number }
  | { kind: "town"; label: string; regionKey: string; regionLabel: string; town: string; count: number }
  | { kind: "club"; label: string; regionKey: string; regionLabel: string; town: string; slug: string };

// Type-ahead for the catalogue search.
//
// With 955 clubs across 19 countries, a text box that only does something when
// you press Enter is a box you have to already know the answer to. Three
// letters should be enough to get to Sicily, to Paje, or to Nikki Beach.
//
// Coasts first, then towns, then individual clubs: a visitor typing "mal" is
// far more likely to mean Mallorca than a club whose name happens to contain
// those letters.
export const suggestPlaces = createServerFn({ method: "GET" })
  .inputValidator(z.object({ q: z.string().max(60) }))
  .handler(async ({ data }): Promise<Suggestion[]> => {
    const q = data.q.trim().toLowerCase();
    if (q.length < 2) return [];
    const { DB } = bindings();
    if (!DB) return [];

    const { results } = await DB.prepare(
      `SELECT slug, name, town, region_key, region_label, country
         FROM clubs WHERE hidden = 0`,
    ).all<{
      slug: string; name: string; town: string;
      region_key: string; region_label: string; country: string;
    }>();
    const rows = results ?? [];

    // Prefix beats contained: "cost" should offer Costa del Sol before it
    // offers a club with "coast" in the middle of its name. Both sides are
    // folded (accents, hyphens, "Ste" for "Sainte"), so "sainte maxime" and
    // "Ste-Maxime" both reach the town the database spells "Sainte-Maxime".
    const rank = (s: string) => rankSearch(s, q);

    const regions = new Map<string, { label: string; country: string; count: number }>();
    const towns = new Map<string, { town: string; region: string; regionKey: string; count: number }>();
    for (const r of rows) {
      const reg = regions.get(r.region_key);
      if (reg) reg.count += 1;
      else regions.set(r.region_key, { label: r.region_label, country: r.country, count: 1 });
      const key = `${r.town}|${r.region_label}`;
      const t = towns.get(key);
      if (t) t.count += 1;
      else towns.set(key, { town: r.town, region: r.region_label, regionKey: r.region_key, count: 1 });
    }

    const out: (Suggestion & { r: number })[] = [];
    for (const [key, v] of regions) {
      const r = Math.min(...[rank(v.label), rank(v.country)].filter((x) => x >= 0), 9);
      if (r < 9) {
        out.push({ kind: "region", label: v.label, regionKey: key, country: v.country, count: v.count, r });
      }
    }
    for (const v of towns.values()) {
      const r = rank(v.town);
      if (r >= 0) {
        out.push({
          kind: "town", label: v.town, regionKey: v.regionKey, regionLabel: v.region,
          town: v.town, count: v.count, r: r + 3,
        });
      }
    }
    for (const r0 of rows) {
      const r = rank(r0.name);
      if (r >= 0) {
        out.push({
          kind: "club", label: r0.name, regionKey: r0.region_key,
          regionLabel: r0.region_label, town: r0.town, slug: r0.slug, r: r + 6,
        });
      }
    }

    return out
      .sort((a, b) => a.r - b.r || a.label.localeCompare(b.label))
      .slice(0, 8)
      .map(({ r: _r, ...s }) => s as Suggestion);
  });

export const searchClubs = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      q: z.string().max(80).optional(),
      region: z.string().max(60).optional(),
      facilities: z.array(z.string().max(40)).max(10).optional(),
      priced: z.boolean().optional(),
      page: z.number().int().min(1).max(200).optional(),
      sort: z.enum(["pick", "rating", "price_asc", "price_desc", "name"]).optional(),
      // The visitor's position, when they asked for clubs near them. Ordering
      // by distance has to happen here, over the whole index, not in the
      // browser over the one page it received: the old client-side sort only
      // reordered the 24 top-ranked clubs of the catalogue, so "near me" from
      // Sainte-Maxime showed Dubai and Marbella and none of the clubs on the
      // beach the visitor was standing on.
      near: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).optional(),
    }),
  )
  .handler(async ({ data }): Promise<SearchResult> => {
    const empty: SearchResult = {
      clubs: [], total: 0, page: 1, pages: 1, facets: [], pricedCount: 0, regions: [],
    };
    const { DB } = bindings();
    if (!DB) return empty;

    const { results: index } = await DB.prepare(
      `SELECT slug, name, town, region_key, region_label, country, price_from,
              facilities, amenities, sponsored, verified, featured,
              google_rating, google_review_count, lat, lng
         FROM clubs WHERE hidden = 0`,
    ).all<{
      slug: string; name: string; town: string; region_key: string;
      region_label: string; country: string; price_from: number | null;
      facilities: string | null; amenities: string | null; sponsored: number;
      verified: number; featured: number; google_rating: number | null;
      google_review_count: number | null; lat: number | null; lng: number | null;
    }>();

    const parseFac = (raw: string | null): string[] => {
      if (!raw) return [];
      try {
        const v = JSON.parse(raw);
        return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
      } catch {
        return [];
      }
    };

    let rows = index ?? [];

    // Built before any filtering, from the full index.
    const regionMap = new Map<string, { key: string; label: string; country: string; count: number }>();
    for (const r of rows) {
      const seen = regionMap.get(r.region_key);
      if (seen) seen.count += 1;
      else
        regionMap.set(r.region_key, {
          key: r.region_key,
          label: r.region_label,
          country: r.country,
          count: 1,
        });
    }
    const regions = [...regionMap.values()].sort(
      (a, b) => a.country.localeCompare(b.country) || b.count - a.count || a.label.localeCompare(b.label),
    );

    if (data.region) rows = rows.filter((r) => r.region_key === data.region);

    // Facets describe the region you are looking at, not what is left after
    // your other filters, so a filter never hides the option next to it.
    const facetsOf = (r: { facilities: string | null; amenities: string | null }) =>
      normalizeFacilityList(parseFac(r.facilities), parseFac(r.amenities));

    const facetCounts = new Map<string, number>();
    for (const r of rows) {
      for (const f of new Set(facetsOf(r))) {
        facetCounts.set(f, (facetCounts.get(f) ?? 0) + 1);
      }
    }
    const pricedCount = rows.filter((r) => r.price_from != null).length;

    if (data.facilities?.length) {
      const want = data.facilities;
      rows = rows.filter((r) => {
        const own = new Set(facetsOf(r));
        return want.every((f) => own.has(f));
      });
    }
    if (data.priced) rows = rows.filter((r) => r.price_from != null);

    // Folded on both sides: "Sainte Maxime", "Ste-Maxime" and "sainte-maxime"
    // are the same search, and "Cote d'Azur" finds "Côte d'Azur". The slug is
    // searched too, because it is where the beach name lives: the town column
    // says "Sainte-Maxime", the slug says "la-nartelle-sainte-maxime", and a
    // visitor on that beach types "Nartelle".
    const q = (data.q ?? "").trim();
    if (q) {
      rows = rows.filter(
        (r) =>
          matchesSearch(r.name, q) ||
          matchesSearch(r.town, q) ||
          matchesSearch(r.slug.replace(/-/g, " "), q) ||
          matchesSearch(r.region_label, q) ||
          matchesSearch(r.country, q),
      );
    }

    // Sponsored first because it is paid for, then the listings that actually
    // carry information, then the ones travellers evidently rate.
    const pick = (a: typeof rows[number], b: typeof rows[number]) =>
      b.sponsored - a.sponsored ||
      b.verified - a.verified ||
      Number(b.price_from != null) - Number(a.price_from != null) ||
      b.featured - a.featured ||
      (b.google_review_count ?? 0) - (a.google_review_count ?? 0) ||
      a.name.localeCompare(b.name);

    // Every explicit sort falls back to `pick` on a tie, and rows missing the
    // sort key go last rather than to one extreme: a club with no price is not
    // the cheapest one, and a club with no rating is not the worst.
    const SORTS: Record<string, (a: typeof rows[number], b: typeof rows[number]) => number> = {
      pick,
      rating: (a, b) =>
        Number(b.google_rating != null) - Number(a.google_rating != null) ||
        (b.google_rating ?? 0) - (a.google_rating ?? 0) ||
        (b.google_review_count ?? 0) - (a.google_review_count ?? 0) ||
        pick(a, b),
      price_asc: (a, b) =>
        Number(b.price_from != null) - Number(a.price_from != null) ||
        (a.price_from ?? 0) - (b.price_from ?? 0) ||
        pick(a, b),
      price_desc: (a, b) =>
        Number(b.price_from != null) - Number(a.price_from != null) ||
        (b.price_from ?? 0) - (a.price_from ?? 0) ||
        pick(a, b),
      name: (a, b) => a.name.localeCompare(b.name),
    };
    if (data.near) {
      const { lat, lng } = data.near;
      const dist = new Map<string, number>();
      for (const r of rows) {
        dist.set(
          r.slug,
          r.lat != null && r.lng != null ? haversineKm(lat, lng, r.lat, r.lng) : Number.POSITIVE_INFINITY,
        );
      }
      // Nearest first; clubs without coordinates go last, in the usual order.
      rows.sort((a, b) => (dist.get(a.slug)! - dist.get(b.slug)!) || pick(a, b));
    } else {
      rows.sort(SORTS[data.sort ?? "pick"] ?? pick);
    }

    const total = rows.length;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const page = Math.min(Math.max(1, data.page ?? 1), pages);
    const slugs = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r) => r.slug);
    if (slugs.length === 0) {
      return { ...empty, total, page, pages, facets: sortFacets(facetCounts), pricedCount, regions };
    }

    const placeholders = slugs.map(() => "?").join(",");
    const { results } = await DB.prepare(
      `${LIST_SELECT} WHERE c.hidden = 0 AND c.slug IN (${placeholders})`,
    )
      .bind(...slugs)
      .all<ClubRow>();

    // The IN clause loses our ordering, so restore it from the page slice.
    const byslug = new Map((results ?? []).map((r) => [r.slug, parseClub(r)]));
    const clubs = slugs.map((s) => byslug.get(s)).filter((c): c is Club => Boolean(c));

    return { clubs, total, page, pages, facets: sortFacets(facetCounts), pricedCount, regions };
  });

function sortFacets(counts: Map<string, number>): [string, number][] {
  return [...counts.entries()]
    .filter(([, n]) => n >= 3)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 14);
}

export type ThemeRegion = {
  regionKey: string;
  regionLabel: string;
  country: string;
  count: number;
};

export type ThemeResult = {
  clubs: Club[];
  /** Regions carrying enough clubs for this theme to justify a page. */
  regions: ThemeRegion[];
  regionLabel: string;
  country: string;
};

// One theme, optionally narrowed to one region.
//
// The filtering has to happen in the worker rather than in SQL: `facilities` is
// a JSON blob written by research in whatever shape the source used, and only
// normalizeFacilities knows that "Dogs allowed", "pet_friendly" and "Accesso
// animali" are the same thing. A LIKE over the raw column would quietly answer
// a different question than the filter chips on /clubs do, and the two would
// drift apart the first time someone added a synonym.
export const clubsByTheme = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      theme: z.string().min(1).max(40),
      regionKey: z.string().max(60).optional(),
    }),
  )
  .handler(async ({ data }): Promise<ThemeResult> => {
    const empty: ThemeResult = { clubs: [], regions: [], regionLabel: "", country: "" };
    const { DB } = bindings();
    if (!DB) return empty;

    const { results } = await DB.prepare(
      `${LIST_SELECT} WHERE c.hidden = 0 ORDER BY c.sponsored DESC, c.verified DESC, c.featured DESC, c.name ASC`,
    ).all<ClubRow>();

    const all = (results ?? []).map(parseClub);
    const matches = all.filter((c) =>
      normalizeFacilityList(c.facilities, c.amenities).includes(data.theme),
    );

    // Counted over every region, not just the one being viewed, because the
    // Europe-wide page uses these as its links and each regional page uses them
    // to offer the same theme on the next coast along.
    const byRegion = new Map<string, ThemeRegion>();
    for (const c of matches) {
      const seen = byRegion.get(c.region_key);
      if (seen) seen.count += 1;
      else
        byRegion.set(c.region_key, {
          regionKey: c.region_key,
          regionLabel: c.region_label,
          country: c.country,
          count: 1,
        });
    }
    const regions = [...byRegion.values()]
      .filter((r) => r.count >= MIN_CLUBS_PER_THEME_PAGE)
      .sort((a, b) => b.count - a.count || a.regionLabel.localeCompare(b.regionLabel));

    const clubs = data.regionKey
      ? matches.filter((c) => c.region_key === data.regionKey)
      : matches;

    return {
      clubs,
      regions,
      regionLabel: clubs[0]?.region_label ?? data.regionKey ?? "",
      country: clubs[0]?.country ?? "",
    };
  });

export const listClubsByRegion = createServerFn({ method: "GET" })
  .inputValidator(z.object({ regionKey: z.string().min(1).max(60) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { clubs: [] as Club[], regionLabel: data.regionKey, country: "" };
    const { results } = await DB.prepare(
      `${LIST_SELECT} WHERE c.hidden = 0 AND c.region_key = ? ORDER BY c.sponsored DESC, c.verified DESC, c.featured DESC, c.name ASC`,
    )
      .bind(data.regionKey)
      .all<ClubRow>();
    const clubs = (results ?? []).map(parseClub);
    return {
      clubs,
      regionLabel: clubs[0]?.region_label ?? data.regionKey,
      country: clubs[0]?.country ?? "",
    };
  });

export const getClubBySlug = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      slug: z.string().min(1).max(120),
      // Which language the write-up should be in. Absent or "en" reads the
      // English source straight off the club row.
      lang: z.string().max(5).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    const none = { club: null as Club | null, socials: [] as ClubSocial[], translated: false };
    if (!DB) return none;
    const row = await DB.prepare(`${CLUB_SELECT} WHERE c.hidden = 0 AND c.slug = ?`)
      .bind(data.slug)
      .first<ClubRow>();
    if (!row) return none;

    // Whether the write-up on this page is in the requested language. English
    // always is. For the other languages the locale route uses this to keep a
    // page that fell back to the English text out of the index: that page is
    // a copy of /club/<slug>, not a translation of it.
    let translated = !data.lang || data.lang === "en";

    // Field-by-field fallback rather than all-or-nothing: a club whose write-up
    // is translated but whose opening hours are not still shows the translated
    // write-up. A missing translation degrades to English, which is honest and
    // readable, instead of to an empty page.
    if (data.lang && data.lang !== "en") {
      const t = await DB.prepare(
        `SELECT long_description, description, opening_hours
           FROM club_translations WHERE club_id = ? AND lang = ?`,
      )
        .bind(row.id, data.lang)
        .first<{
          long_description: string | null;
          description: string | null;
          opening_hours: string | null;
        }>();
      if (t) {
        translated = !!t.long_description;
        row.long_description = t.long_description ?? row.long_description;
        row.description = t.description ?? row.description;
        row.opening_hours = t.opening_hours ?? row.opening_hours;
      }
    }
    // The channels the club publishes on its own site. Ordered the way a beach
    // visitor uses them, not alphabetically.
    const { results: socials } = await DB.prepare(
      `SELECT network, handle, url FROM club_socials WHERE club_slug = ?
        ORDER BY CASE network
                   WHEN 'instagram' THEN 1 WHEN 'facebook' THEN 2
                   WHEN 'tiktok' THEN 3 ELSE 4 END`,
    )
      .bind(data.slug)
      .all<ClubSocial>();

    return { club: parseClub(row), socials: (socials ?? []) as ClubSocial[], translated };
  });

export const createAvailabilityRequest = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      clubSlug: z.string().min(1).max(120),
      name: z.string().min(1).max(120),
      contact: z.string().min(3).max(200),
      visitDate: z.string().min(4).max(40),
      partySize: z.number().int().min(1).max(40),
      message: z.string().max(1000).optional().default(""),
      locale: z.string().max(8).optional().default("en"),
      // Which channel the traveller was handed to, and the page they were on.
      // Both describe the club, not the person: no IP, no device, no cookie.
      channel: z.enum(["whatsapp", "email", "phone"]).optional(),
      pagePath: z.string().max(200).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const club = await DB.prepare("SELECT id FROM clubs WHERE slug = ?")
      .bind(data.clubSlug)
      .first<{ id: number }>();
    if (!club) return { ok: false as const };

    // The form now disables its own button, but that only covers the screen.
    // A double tap on a slow phone, a back button, a tab iOS froze and
    // restored: all of those still arrive here twice, and every extra row is a
    // lead the club owner has to work out is the same person.
    //
    // Matching on name *or* contact, not both, because the pair we actually saw
    // had the contact corrected between the two tries (an address missing its
    // .com). Same club and same date is what makes that safe: two different
    // travelers named the same thing asking the same club about the same day
    // inside five minutes is not a case worth splitting rows over.
    //
    // And we update rather than ignore. The second submit is usually the
    // corrected one, so keeping the first would preserve exactly the typo the
    // traveler came back to fix.
    const recent = await DB.prepare(
      `SELECT id FROM availability_requests
        WHERE club_slug = ? AND visit_date = ? AND (name = ? OR contact = ?)
          AND created_at >= datetime('now', '-5 minutes')
        ORDER BY created_at DESC
        LIMIT 1`,
    )
      .bind(data.clubSlug, data.visitDate, data.name, data.contact)
      .first<{ id: number }>();

    if (recent) {
      await DB.prepare(
        `UPDATE availability_requests
            SET name = ?, contact = ?, party_size = ?, message = ?, locale = ?,
                channel = ?, page_path = ?
          WHERE id = ?`,
      )
        .bind(
          data.name,
          data.contact,
          data.partySize,
          data.message,
          data.locale,
          data.channel ?? null,
          data.pagePath ?? null,
          recent.id,
        )
        .run();
      return { ok: true as const };
    }

    await DB.prepare(
      `INSERT INTO availability_requests
        (club_id, club_slug, name, contact, visit_date, party_size, message, locale,
         channel, page_path)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    )
      .bind(
        club.id,
        data.clubSlug,
        data.name,
        data.contact,
        data.visitDate,
        data.partySize,
        data.message,
        data.locale,
        data.channel ?? null,
        data.pagePath ?? null,
      )
      .run();
    return { ok: true as const };
  });

// Nothing is charged automatically yet (no payment processor is wired up:
// travelers still pay the club directly, as shown in the UI). This is the
// internal accounting layer only: an estimate of what lidoo's cut would be
// per reservation, visible in /admin, so the platform owner can see revenue
// potential today and invoice clubs manually while a real checkout is
// pending. 8% of the booking value, clamped to a sane EUR 2-15 range.
function estimateFeeCents(priceEur: number, spotCount: number): number {
  const raw = Math.round(priceEur * spotCount * 0.08 * 100);
  return Math.min(1500, Math.max(200, raw));
}

export const getSpotAvailability = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      clubSlug: z.string().min(1).max(120),
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { taken: [] as string[], closed: false };
    // A bed is unavailable for two different reasons — someone booked it, or
    // the club blocked it — and the traveler does not care which. Both are
    // folded into one list so the map can't offer a bed the club has already
    // given away. A whole-day closure is reported separately so the UI can say
    // "closed" instead of drawing every bed as taken.
    const [reserved, blocks] = await Promise.all([
      DB.prepare(
        "SELECT spot_id FROM spot_reservations WHERE club_slug = ? AND visit_date = ? AND status != 'cancelled'",
      )
        .bind(data.clubSlug, data.date)
        .all<{ spot_id: string }>(),
      DB.prepare(
        "SELECT scope, spot_id FROM availability_blocks WHERE club_slug = ? AND block_date = ?",
      )
        .bind(data.clubSlug, data.date)
        .all<{ scope: string; spot_id: string | null }>(),
    ]);
    const blockRows = blocks.results ?? [];
    const closed = blockRows.some((b) => b.scope === "day");
    const taken = [
      ...(reserved.results ?? []).map((r) => r.spot_id),
      ...blockRows.filter((b) => b.scope === "spot" && b.spot_id).map((b) => b.spot_id as string),
    ];
    return { taken: [...new Set(taken)], closed };
  });

export const createSpotReservation = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      clubSlug: z.string().min(1).max(120),
      spotIds: z.array(z.string().min(2).max(6)).min(1).max(4),
      visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      name: z.string().min(1).max(120),
      contact: z.string().min(3).max(200),
      partySize: z.number().int().min(1).max(40),
      locale: z.string().max(8).optional().default("en"),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const, conflict: false };
    const club = await DB.prepare("SELECT layout FROM clubs WHERE slug = ?")
      .bind(data.clubSlug)
      .first<{ layout: string | null }>();
    if (!club?.layout) return { ok: false as const, conflict: false };

    // Validate requested spots against the club's actual layout.
    let valid = new Set<string>();
    let layout: BeachLayout | null = null;
    try {
      layout = JSON.parse(club.layout) as BeachLayout;
      for (const row of layout.rows) {
        for (let i = 1; i <= row.spots; i++) valid.add(`${row.id}${i}`);
      }
    } catch {
      return { ok: false as const, conflict: false };
    }
    if (data.spotIds.some((s) => !valid.has(s))) {
      return { ok: false as const, conflict: false };
    }

    // Enforce the club's own closures here, not just in the map. Hiding a bed
    // in the UI stops an honest visitor; it does not stop a stale tab or a
    // direct call to this endpoint, and either would hand the club a booking
    // for a day it has already closed.
    const { results: blockRows } = await DB.prepare(
      "SELECT scope, spot_id FROM availability_blocks WHERE club_slug = ? AND block_date = ?",
    )
      .bind(data.clubSlug, data.visitDate)
      .all<{ scope: string; spot_id: string | null }>();
    const blocks = blockRows ?? [];
    if (blocks.some((b) => b.scope === "day")) {
      return { ok: false as const, conflict: true };
    }
    const blockedSpots = new Set(
      blocks.filter((b) => b.scope === "spot" && b.spot_id).map((b) => b.spot_id as string),
    );
    if (data.spotIds.some((s) => blockedSpots.has(s))) {
      return { ok: false as const, conflict: true };
    }

    const feeCents = layout ? estimateFeeCents(layout.price, data.spotIds.length) : 0;
    const groupCode = crypto.randomUUID().slice(0, 8).toUpperCase();
    try {
      await DB.batch(
        data.spotIds.map((spotId) =>
          DB.prepare(
            `INSERT INTO spot_reservations
              (group_code, club_slug, spot_id, visit_date, name, contact, party_size, locale, estimated_fee_cents)
             VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          ).bind(
            groupCode,
            data.clubSlug,
            spotId,
            data.visitDate,
            data.name,
            data.contact,
            data.partySize,
            data.locale,
            feeCents,
          ),
        ),
      );
    } catch {
      // UNIQUE violation: someone reserved one of these spots in the meantime.
      return { ok: false as const, conflict: true };
    }
    return { ok: true as const, conflict: false, groupCode };
  });

// --- Reviews ---------------------------------------------------------------
// TripAdvisor's own model for listings with no verified booking: reviewers
// self-attest their name and when they visited, no proof of a Sunbedly
// transaction required (most bookings still happen over WhatsApp, outside
// the platform). Reviews publish immediately; Sam can hide any of them from
// /admin, which is the realistic moderation model at this scale (no
// dedicated review team, but nothing bad sits live for long either).

export type Review = {
  id: number;
  author_name: string;
  rating: number;
  title: string;
  body: string;
  visit_month: string;
  booked_via: string;
  created_at: string;
};

export const getClubReviews = createServerFn({ method: "GET" })
  .inputValidator(z.object({ clubSlug: z.string().min(1).max(120) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { reviews: [] as Review[] };
    const { results } = await DB.prepare(
      `SELECT id, author_name, rating, title, body, visit_month, booked_via, created_at
       FROM reviews WHERE club_slug = ? AND status = 'published'
       ORDER BY created_at DESC LIMIT 200`,
    )
      .bind(data.clubSlug)
      .all<Review>();
    return { reviews: results ?? [] };
  });

const BOOKED_VIA = ["whatsapp", "lidoo", "phone", "walkin", "other"] as const;

// One-way, so the table can enforce "one review per person per club" without
// keeping a readable email address around for the life of the listing. Salted
// with a fixed application string so the digests are not a rainbow-table lookup
// of every email address in existence.
async function contactHash(contact: string): Promise<string> {
  const normalized = contact.trim().toLowerCase();
  const bytes = new TextEncoder().encode(`sunbedly-review:${normalized}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const createReview = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      clubSlug: z.string().min(1).max(120),
      authorName: z.string().min(1).max(120),
      authorContact: z.string().min(3).max(200),
      rating: z.number().int().min(1).max(5),
      title: z.string().max(140).optional().default(""),
      body: z.string().min(20).max(2000),
      visitMonth: z.string().min(4).max(7),
      bookedVia: z.enum(BOOKED_VIA).optional().default("other"),
      locale: z.string().max(8).optional().default("en"),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const, reason: "server" as const };

    // Simple, no-links spam guard. Legitimate reviews never need a URL.
    if (/https?:\/\/|www\./i.test(data.body) || /https?:\/\/|www\./i.test(data.title)) {
      return { ok: false as const, reason: "spam" as const };
    }

    const club = await DB.prepare("SELECT id FROM clubs WHERE slug = ?")
      .bind(data.clubSlug)
      .first<{ id: number }>();
    if (!club) return { ok: false as const, reason: "server" as const };

    try {
      await DB.prepare(
        `INSERT INTO reviews
          (club_id, club_slug, author_name, contact_hash, rating, title, body, visit_month, booked_via, locale)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      )
        .bind(
          club.id,
          data.clubSlug,
          data.authorName,
          await contactHash(data.authorContact),
          data.rating,
          data.title,
          data.body,
          data.visitMonth,
          data.bookedVia,
          data.locale,
        )
        .run();
    } catch {
      // UNIQUE(club_id, contact_hash): this contact already reviewed this club.
      return { ok: false as const, reason: "duplicate" as const };
    }
    return { ok: true as const };
  });

// --- Admin console (Sam only) --------------------------------------------
// No user accounts, no sessions: a single shared secret (ADMIN_PASSWORD,
// set via .dev.vars locally / `wrangler secret put` in production) gates a
// read-only overview of every lead the platform has generated. Every admin
// call re-checks the password server-side; nothing here is exposed unless it
// matches.

function checkAdminPassword(db: unknown, password: string): boolean {
  const { ADMIN_PASSWORD } = bindings();
  return Boolean(ADMIN_PASSWORD) && password === ADMIN_PASSWORD && Boolean(db);
}

const MAX_ADMIN_FAILURES = 5;
const ADMIN_LOCKOUT_MINUTES = 15;
// One admin, one counter. Throttling per IP would be better, but a server
// function in this framework has no clean, unspoofable way to read
// CF-Connecting-IP, and a counter keyed on a client-supplied value is theatre.
// Since exactly one person logs in here, a global counter closes the same hole:
// five misses and the door is shut for fifteen minutes.
//
// The trade-off, stated plainly: someone can lock Sam out for a quarter of an
// hour by guessing wrong on purpose. That beats letting them guess forever at
// a console that lists travellers' names and phone numbers.
const ADMIN_COUNTER_KEY = "admin";

export const adminLogin = createServerFn({ method: "POST" })
  .inputValidator(z.object({ password: z.string().min(1).max(200) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const, blocked: false };

    const row = await DB.prepare(
      `SELECT failures FROM admin_attempts
        WHERE ip = ? AND last_at > datetime('now', '-${ADMIN_LOCKOUT_MINUTES} minutes')`,
    )
      .bind(ADMIN_COUNTER_KEY)
      .first<{ failures: number }>();

    if ((row?.failures ?? 0) >= MAX_ADMIN_FAILURES) {
      return { ok: false as const, blocked: true };
    }

    const ok = checkAdminPassword(DB, data.password);
    if (ok) {
      await DB.prepare("DELETE FROM admin_attempts WHERE ip = ?").bind(ADMIN_COUNTER_KEY).run();
    } else {
      await DB.prepare(
        `INSERT INTO admin_attempts (ip, failures, last_at) VALUES (?, 1, datetime('now'))
         ON CONFLICT(ip) DO UPDATE SET
           failures = CASE WHEN last_at > datetime('now', '-${ADMIN_LOCKOUT_MINUTES} minutes')
                           THEN failures + 1 ELSE 1 END,
           last_at = datetime('now')`,
      )
        .bind(ADMIN_COUNTER_KEY)
        .run();
    }
    return { ok, blocked: false };
  });

export type AdminOverview = {
  totals: {
    requests: number;
    reservationGroups: number;
    applications: number;
    reviews: number;
    estimatedFeeCentsTotal: number;
    estimatedFeeCentsThisMonth: number;
  };
  requests: {
    id: number;
    club_slug: string;
    club_name: string;
    name: string;
    contact: string;
    visit_date: string;
    party_size: number;
    message: string;
    created_at: string;
  }[];
  reservations: {
    group_code: string;
    club_slug: string;
    club_name: string;
    visit_date: string;
    name: string;
    contact: string;
    party_size: number;
    spots: string;
    estimated_fee_cents: number;
    created_at: string;
  }[];
  applications: {
    id: number;
    club_name: string;
    contact_name: string;
    contact: string;
    country: string;
    town: string;
    instagram: string;
    has_booking_system: string;
    message: string;
    created_at: string;
  }[];
  clubs: {
    slug: string;
    name: string;
    town: string;
    region_label: string;
    verified: number;
    featured: number;
    sponsored: number;
  }[];
  reviews: {
    id: number;
    club_slug: string;
    club_name: string;
    author_name: string;
    rating: number;
    title: string;
    body: string;
    visit_month: string;
    booked_via: string;
    status: string;
    created_at: string;
  }[];
  photos: {
    id: number;
    club_slug: string;
    club_name: string;
    r2_key: string;
    uploaded_by_name: string;
    status: string;
    created_at: string;
  }[];
};

export const getAdminOverview = createServerFn({ method: "POST" })
  .inputValidator(z.object({ password: z.string().min(1).max(200) }))
  .handler(async ({ data }): Promise<{ ok: false } | ({ ok: true } & AdminOverview)> => {
    const { DB } = bindings();
    if (!checkAdminPassword(DB, data.password) || !DB) return { ok: false };

    const [
      requestsRes,
      reservationsRes,
      applicationsRes,
      feeTotalRes,
      feeMonthRes,
      countsRes,
      clubsRes,
      reviewsRes,
      photosRes,
    ] = await Promise.all([
        DB.prepare(
          `SELECT r.id, r.club_slug, c.name AS club_name, r.name, r.contact, r.visit_date,
                  r.party_size, r.message, r.created_at
           FROM availability_requests r
           LEFT JOIN clubs c ON c.slug = r.club_slug
           ORDER BY r.created_at DESC LIMIT 100`,
        ).all(),
        DB.prepare(
          `SELECT s.group_code, s.club_slug, c.name AS club_name, s.visit_date, s.name,
                  s.contact, s.party_size, s.estimated_fee_cents, MIN(s.created_at) AS created_at,
                  GROUP_CONCAT(s.spot_id) AS spots
           FROM spot_reservations s
           LEFT JOIN clubs c ON c.slug = s.club_slug
           GROUP BY s.group_code
           ORDER BY created_at DESC LIMIT 100`,
        ).all(),
        DB.prepare(
          `SELECT id, club_name, contact_name, contact, country, town, instagram,
                  has_booking_system, message, created_at
           FROM partner_applications ORDER BY created_at DESC LIMIT 100`,
        ).all(),
        DB.prepare(
          `SELECT COALESCE(SUM(fee), 0) AS total FROM (
             SELECT MAX(estimated_fee_cents) AS fee FROM spot_reservations GROUP BY group_code
           )`,
        ).first<{ total: number }>(),
        DB.prepare(
          `SELECT COALESCE(SUM(fee), 0) AS total FROM (
             SELECT MAX(estimated_fee_cents) AS fee FROM spot_reservations
             WHERE created_at >= datetime('now', 'start of month') GROUP BY group_code
           )`,
        ).first<{ total: number }>(),
        DB.prepare(
          `SELECT
             (SELECT COUNT(*) FROM availability_requests) AS requests,
             (SELECT COUNT(DISTINCT group_code) FROM spot_reservations) AS reservationGroups,
             (SELECT COUNT(*) FROM partner_applications) AS applications,
             (SELECT COUNT(*) FROM reviews) AS reviews`,
        ).first<{
          requests: number;
          reservationGroups: number;
          applications: number;
          reviews: number;
        }>(),
        DB.prepare(
          `SELECT slug, name, town, region_label, verified, featured, sponsored
           FROM clubs ORDER BY sponsored DESC, verified DESC, featured DESC, name ASC`,
        ).all(),
        DB.prepare(
          `SELECT rv.id, rv.club_slug, c.name AS club_name, rv.author_name, rv.rating, rv.title,
                  rv.body, rv.visit_month, rv.booked_via, rv.status, rv.created_at
           FROM reviews rv
           LEFT JOIN clubs c ON c.slug = rv.club_slug
           ORDER BY rv.created_at DESC LIMIT 150`,
        ).all(),
        DB.prepare(
          `SELECT p.id, p.club_slug, c.name AS club_name, p.r2_key, p.uploaded_by_name,
                  p.status, p.created_at
           FROM club_photos p
           LEFT JOIN clubs c ON c.slug = p.club_slug
           ORDER BY p.created_at DESC LIMIT 150`,
        ).all(),
      ]);

    return {
      ok: true,
      totals: {
        requests: countsRes?.requests ?? 0,
        reservationGroups: countsRes?.reservationGroups ?? 0,
        applications: countsRes?.applications ?? 0,
        reviews: countsRes?.reviews ?? 0,
        estimatedFeeCentsTotal: feeTotalRes?.total ?? 0,
        estimatedFeeCentsThisMonth: feeMonthRes?.total ?? 0,
      },
      requests: (requestsRes.results ?? []) as AdminOverview["requests"],
      reservations: (reservationsRes.results ?? []) as AdminOverview["reservations"],
      applications: (applicationsRes.results ?? []) as AdminOverview["applications"],
      clubs: (clubsRes.results ?? []) as AdminOverview["clubs"],
      reviews: (reviewsRes.results ?? []) as AdminOverview["reviews"],
      photos: (photosRes.results ?? []) as AdminOverview["photos"],
    };
  });

export type ClubDemandRow = {
  club_slug: string;
  club_name: string;
  town: string;
  region_label: string;
  requests: number;
  beds: number;
  /** "it 4 / nl 2", most-used first. Tells you which market wants this club. */
  by_locale: string;
  whatsapp: number;
  email: number;
  first_at: string;
  last_at: string;
  has_email: number;
  claimed: number;
};

// Which clubs actually generate demand.
//
// The requests table has always known this and nothing ever asked it. It is the
// most useful list on the platform for two reasons. It tells you where to spend
// outreach: a club that already has eleven requests waiting is a far easier
// conversation than a cold one. And it is the pitch itself, because "fourteen
// people asked us about you this month" is an argument and "you are listed with
// us" is not.
//
// Grouped by slug rather than club_id so a club that was re-imported under a new
// id still shows its full history against the page travellers actually visited.
export type InsightsDay = { day: string; clicks: number; requests: number };
export type InsightsClub = {
  slug: string;
  name: string;
  town: string;
  region_label: string;
  clicks: number;
  requests: number;
  has_email: number;
  has_whatsapp: number;
  has_phone: number;
  claimed: number;
};
export type InsightsRegion = {
  region_key: string;
  region_label: string;
  country: string;
  clubs: number;
  clicks: number;
  requests: number;
};
export type Insights = {
  ok: boolean;
  days: InsightsDay[];
  clubs: InsightsClub[];
  regions: InsightsRegion[];
  totals: {
    clicksWeek: number;
    clicksPrevWeek: number;
    requestsWeek: number;
    requestsPrevWeek: number;
    clubsWithClicks: number;
    clubsWithRequests: number;
  };
  health: {
    clubs: number;
    regions: number;
    countries: number;
    withPrice: number;
    withPhoto: number;
    withDescription: number;
    reachable: number;
    claimed: number;
    withSocials: number;
  };
  /** Clubs that got traffic this month but have no way to be contacted. */
  unreachableWithTraffic: { slug: string; name: string; region_label: string; clicks: number }[];
  photoBudget: { month: string; fetches: number } | null;
};

// The operator's view of the business.
//
// The one number that mattered and was nowhere on screen: outbound clicks.
// There are roughly forty of those for every request, and they are what a club
// is actually being sold ("you got 28 visitors from us last week"). Requests
// alone made the site look forty times smaller than it is.
//
// Everything is scoped to what we log ourselves. Sessions live in GA4 and are
// deliberately not mirrored here: two sources of truth for the same number is
// how you end up quoting the wrong one.
export const getInsights = createServerFn({ method: "POST" })
  .inputValidator(z.object({ password: z.string().min(1).max(200) }))
  .handler(async ({ data }): Promise<Insights> => {
    const empty: Insights = {
      ok: false, days: [], clubs: [], regions: [],
      totals: {
        clicksWeek: 0, clicksPrevWeek: 0, requestsWeek: 0,
        requestsPrevWeek: 0, clubsWithClicks: 0, clubsWithRequests: 0,
      },
      health: {
        clubs: 0, regions: 0, countries: 0, withPrice: 0,
        withPhoto: 0, withDescription: 0, reachable: 0, claimed: 0, withSocials: 0,
      },
      unreachableWithTraffic: [],
      photoBudget: null,
    };
    const { DB } = bindings();
    if (!checkAdminPassword(DB, data.password) || !DB) return empty;

    const [days, clubs, regions, totals, health, unreachable, budget] = await DB.batch([
      // Clicks and requests share one row per day, so the chart cannot drift
      // between two series that were counted over different windows.
      DB.prepare(
        `WITH d(day) AS (
           SELECT date('now', '-29 days')
           UNION ALL SELECT date(day, '+1 day') FROM d WHERE day < date('now')
         )
         SELECT d.day,
                COALESCE((SELECT SUM(clicks) FROM club_outbound_clicks o WHERE o.day = d.day), 0) AS clicks,
                COALESCE((SELECT COUNT(*) FROM availability_requests r
                           WHERE date(r.created_at) = d.day AND r.source = 'traveler'), 0) AS requests
           FROM d ORDER BY d.day`,
      ),
      DB.prepare(
        `SELECT c.slug, c.name, c.town, c.region_label,
                COALESCE((SELECT SUM(o.clicks) FROM club_outbound_clicks o WHERE o.club_slug = c.slug), 0) AS clicks,
                COALESCE((SELECT COUNT(*) FROM availability_requests r
                           WHERE r.club_slug = c.slug AND r.source = 'traveler'), 0) AS requests,
                (c.email IS NOT NULL) AS has_email,
                (c.whatsapp IS NOT NULL) AS has_whatsapp,
                (c.phone IS NOT NULL) AS has_phone,
                c.verified AS claimed
           FROM clubs c WHERE c.hidden = 0
          ORDER BY clicks DESC, requests DESC LIMIT 40`,
      ),
      DB.prepare(
        `SELECT c.region_key, c.region_label, c.country, COUNT(*) AS clubs,
                COALESCE(SUM((SELECT SUM(o.clicks) FROM club_outbound_clicks o WHERE o.club_slug = c.slug)), 0) AS clicks,
                COALESCE(SUM((SELECT COUNT(*) FROM availability_requests r
                               WHERE r.club_slug = c.slug AND r.source = 'traveler')), 0) AS requests
           FROM clubs c WHERE c.hidden = 0
          GROUP BY c.region_key ORDER BY clicks DESC`,
      ),
      DB.prepare(
        `SELECT
           (SELECT COALESCE(SUM(clicks),0) FROM club_outbound_clicks WHERE day >= date('now','-6 days')) AS clicksWeek,
           (SELECT COALESCE(SUM(clicks),0) FROM club_outbound_clicks
             WHERE day >= date('now','-13 days') AND day < date('now','-6 days')) AS clicksPrevWeek,
           (SELECT COUNT(*) FROM availability_requests
             WHERE source='traveler' AND date(created_at) >= date('now','-6 days')) AS requestsWeek,
           (SELECT COUNT(*) FROM availability_requests
             WHERE source='traveler' AND date(created_at) >= date('now','-13 days')
               AND date(created_at) < date('now','-6 days')) AS requestsPrevWeek,
           (SELECT COUNT(DISTINCT club_slug) FROM club_outbound_clicks) AS clubsWithClicks,
           (SELECT COUNT(DISTINCT club_slug) FROM availability_requests WHERE source='traveler') AS clubsWithRequests`,
      ),
      DB.prepare(
        `SELECT COUNT(*) AS clubs,
                COUNT(DISTINCT region_key) AS regions,
                COUNT(DISTINCT country) AS countries,
                SUM(price_from IS NOT NULL) AS withPrice,
                SUM(EXISTS(SELECT 1 FROM place_photos p WHERE p.club_slug = clubs.slug)
                    OR image IS NOT NULL) AS withPhoto,
                SUM(description <> '') AS withDescription,
                SUM(email IS NOT NULL OR whatsapp IS NOT NULL OR phone IS NOT NULL) AS reachable,
                SUM(verified = 1) AS claimed,
                SUM(EXISTS(SELECT 1 FROM club_socials cs WHERE cs.club_slug = clubs.slug))
                  AS withSocials
           FROM clubs WHERE hidden = 0`,
      ),
      // Traffic we cannot convert: someone wanted this club and we had no way
      // to put them through. Every row here is a research job worth doing.
      DB.prepare(
        `SELECT c.slug, c.name, c.region_label, SUM(o.clicks) AS clicks
           FROM club_outbound_clicks o JOIN clubs c ON c.slug = o.club_slug
          WHERE c.hidden = 0 AND c.email IS NULL AND c.whatsapp IS NULL AND c.phone IS NULL
          GROUP BY c.slug ORDER BY clicks DESC LIMIT 15`,
      ),
      DB.prepare(
        `SELECT month, fetches FROM photo_fetch_budget ORDER BY month DESC LIMIT 1`,
      ),
    ]);

    return {
      ok: true,
      days: (days.results ?? []) as InsightsDay[],
      clubs: (clubs.results ?? []) as InsightsClub[],
      regions: (regions.results ?? []) as InsightsRegion[],
      totals: (totals.results?.[0] ?? empty.totals) as Insights["totals"],
      health: (health.results?.[0] ?? empty.health) as Insights["health"],
      unreachableWithTraffic: (unreachable.results ?? []) as Insights["unreachableWithTraffic"],
      photoBudget: (budget.results?.[0] ?? null) as Insights["photoBudget"],
    };
  });

export const getClubDemand = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      password: z.string().min(1).max(200),
      days: z.number().int().min(1).max(3650).optional(),
      limit: z.number().int().min(1).max(500).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!checkAdminPassword(DB, data.password) || !DB) {
      return { ok: false as const, rows: [] as ClubDemandRow[] };
    }
    const since = data.days
      ? `AND r.created_at >= datetime('now', '-${data.days} days')`
      : "";
    const { results } = await DB.prepare(
      `SELECT r.club_slug,
              c.name AS club_name,
              c.town,
              c.region_label,
              COUNT(*) AS requests,
              COALESCE(SUM(r.party_size), 0) AS beds,
              (SELECT GROUP_CONCAT(pair, ' / ') FROM (
                 SELECT r2.locale || ' ' || COUNT(*) AS pair
                   FROM availability_requests r2
                  WHERE r2.club_slug = r.club_slug ${since.replace(/r\./g, "r2.")}
                  GROUP BY r2.locale
                  ORDER BY COUNT(*) DESC
               )) AS by_locale,
              SUM(CASE WHEN r.channel = 'whatsapp' THEN 1 ELSE 0 END) AS whatsapp,
              SUM(CASE WHEN r.channel = 'email' THEN 1 ELSE 0 END) AS email,
              MIN(r.created_at) AS first_at,
              MAX(r.created_at) AS last_at,
              (c.email IS NOT NULL) AS has_email,
              c.verified AS claimed
         FROM availability_requests r
         JOIN clubs c ON c.slug = r.club_slug
        WHERE r.source = 'traveler' ${since}
        GROUP BY r.club_slug
        ORDER BY requests DESC, beds DESC
        LIMIT ?`,
    )
      .bind(data.limit ?? 100)
      .all<ClubDemandRow>();
    return { ok: true as const, rows: results ?? [] };
  });

export const setReviewStatus = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      password: z.string().min(1).max(200),
      id: z.number().int(),
      status: z.enum(["published", "hidden"]),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!checkAdminPassword(DB, data.password) || !DB) return { ok: false as const };
    await DB.prepare("UPDATE reviews SET status = ? WHERE id = ?").bind(data.status, data.id).run();
    return { ok: true as const };
  });

// Approve: sets this as the club's live photo (clubs.image) and marks the
// upload approved. Reject: deletes the R2 object and the row outright, no
// point keeping rejected uploads around.
export const setPhotoStatus = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      password: z.string().min(1).max(200),
      id: z.number().int(),
      action: z.enum(["approve", "reject"]),
    }),
  )
  .handler(async ({ data }) => {
    const { DB, STORAGE } = bindings();
    if (!checkAdminPassword(DB, data.password) || !DB) return { ok: false as const };
    const photo = await DB.prepare("SELECT club_slug, r2_key FROM club_photos WHERE id = ?")
      .bind(data.id)
      .first<{ club_slug: string; r2_key: string }>();
    if (!photo) return { ok: false as const };

    if (data.action === "approve") {
      await DB.batch([
        DB.prepare("UPDATE club_photos SET status = 'approved' WHERE id = ?").bind(data.id),
        // A photo approved here came through the site form, so it carries no
        // licence credit; clear whatever the previous cover had.
        DB.prepare(
          "UPDATE clubs SET image = ?, image_credit = NULL, image_credit_url = NULL WHERE slug = ?",
        ).bind(`/api/photo/${photo.r2_key}`, photo.club_slug),
      ]);
    } else {
      if (STORAGE) await STORAGE.delete(`club-photos/${photo.r2_key}`);
      await DB.prepare("DELETE FROM club_photos WHERE id = ?").bind(data.id).run();
    }
    return { ok: true as const };
  });

export const setClubSponsored = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({ password: z.string().min(1).max(200), slug: z.string().min(1).max(120), sponsored: z.boolean() }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!checkAdminPassword(DB, data.password) || !DB) return { ok: false as const };
    await DB.prepare("UPDATE clubs SET sponsored = ? WHERE slug = ?")
      .bind(data.sponsored ? 1 : 0, data.slug)
      .run();
    return { ok: true as const };
  });

export const createPartnerApplication = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      clubName: z.string().min(1).max(160),
      contactName: z.string().min(1).max(120),
      contact: z.string().min(3).max(200),
      country: z.string().max(80).optional().default(""),
      town: z.string().max(120).optional().default(""),
      instagram: z.string().max(120).optional().default(""),
      hasBookingSystem: z.enum(["yes", "no", "unknown"]).optional().default("unknown"),
      message: z.string().max(1500).optional().default(""),
      locale: z.string().max(8).optional().default("en"),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    await DB.prepare(
      `INSERT INTO partner_applications
        (club_name, contact_name, contact, country, town, instagram, has_booking_system, message, locale)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    )
      .bind(
        data.clubName,
        data.contactName,
        data.contact,
        data.country,
        data.town,
        data.instagram,
        data.hasBookingSystem,
        data.message,
        data.locale,
      )
      .run();
    return { ok: true as const };
  });

// ---------------------------------------------------------------------------
// Club-side dashboard.
//
// Everything below is scoped by a per-club manage token. The token is the only
// credential, so every query filters on it directly rather than trusting a
// club_slug that arrived from the client — otherwise one club could read
// another's guest contact details by editing the URL.
// ---------------------------------------------------------------------------

export type ManageRequest = {
  id: number;
  name: string;
  contact: string;
  visit_date: string;
  party_size: number;
  message: string | null;
  status: string;
  created_at: string;
  /** "traveler" when it came through us, "club" when the club logged it. */
  source: string;
  club_note: string | null;
};

export type ManageReservation = {
  id: number;
  group_code: string;
  spot_id: string;
  visit_date: string;
  name: string;
  contact: string;
  party_size: number;
  status: string;
  created_at: string;
};

type Db = NonNullable<ReturnType<typeof bindings>["DB"]>;

async function clubByToken(DB: Db, token: string) {
  return await DB.prepare(`${CLUB_SELECT} WHERE c.manage_token = ? LIMIT 1`)
    .bind(token)
    .first<ClubRow>();
}

export const getClubManageView = createServerFn({ method: "POST" })
  .inputValidator(z.object({ token: z.string().min(16).max(64) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const row = await clubByToken(DB, data.token);
    if (!row) return { ok: false as const };
    const club = parseClub(row);

    const [requests, reservations, photos] = await Promise.all([
      DB.prepare(
        `SELECT id, name, contact, visit_date, party_size, message, status, created_at,
                source, club_note
           FROM availability_requests WHERE club_id = ?
           ORDER BY visit_date DESC, created_at DESC LIMIT 200`,
      )
        .bind(club.id)
        .all<ManageRequest>(),
      DB.prepare(
        `SELECT id, group_code, spot_id, visit_date, name, contact, party_size, status, created_at
           FROM spot_reservations WHERE club_slug = ? ORDER BY created_at DESC LIMIT 100`,
      )
        .bind(club.slug)
        .all<ManageReservation>(),
      DB.prepare(
        `SELECT id, r2_key, status, created_at FROM club_photos
          WHERE club_id = ? ORDER BY created_at DESC LIMIT 30`,
      )
        .bind(club.id)
        .all<{ id: number; r2_key: string; status: string; created_at: string }>(),
    ]);

    // Best-effort: records that the outreach landed and the club is engaged.
    await DB.prepare(`UPDATE clubs SET manage_last_seen = datetime('now') WHERE id = ?`)
      .bind(club.id)
      .run();

    return {
      ok: true as const,
      club,
      requests: requests.results ?? [],
      reservations: reservations.results ?? [],
      photos: photos.results ?? [],
    };
  });


// A booking the club took itself, on the phone or at the counter.
//
// This is what turns a listing into a booking book. Most beds on this coast are
// still sold by telephone, and a system that only knows about the ones we sent
// is a system the owner has to keep reconciling against paper. One list or
// nothing.
export const addClubBooking = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      token: z.string().min(16).max(64),
      name: z.string().trim().min(1).max(80),
      contact: z.string().trim().max(120).optional(),
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      beds: z.number().int().min(1).max(60),
      note: z.string().trim().max(300).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const club = await clubByToken(DB, data.token);
    if (!club) return { ok: false as const };

    await DB.prepare(
      `INSERT INTO availability_requests
         (club_id, club_slug, name, contact, visit_date, party_size, message,
          status, locale, source, beds, club_note)
       VALUES (?, ?, ?, ?, ?, ?, NULL, 'confirmed', 'club', 'club', ?, ?)`,
    )
      .bind(club.id, club.slug, data.name, data.contact ?? "", data.date,
            data.beds, data.beds, data.note ?? null)
      .run();

    return { ok: true as const };
  });

// Undo. Owners mistype dates and phone numbers, and a booking book you cannot
// correct is one they stop trusting after the first mistake.
export const removeClubBooking = createServerFn({ method: "POST" })
  .inputValidator(z.object({ token: z.string().min(16).max(64), id: z.number().int() }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const club = await clubByToken(DB, data.token);
    if (!club) return { ok: false as const };
    // Scoped to the club and to rows the club entered: a token must never be
    // able to delete a traveller's request and the evidence with it.
    await DB.prepare(
      "DELETE FROM availability_requests WHERE id = ? AND club_id = ? AND source = 'club'",
    )
      .bind(data.id, club.id)
      .run();
    return { ok: true as const };
  });

export const setRequestStatus = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      token: z.string().min(16).max(64),
      id: z.number().int().positive(),
      status: z.enum(["new", "confirmed", "declined"]),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const row = await clubByToken(DB, data.token);
    if (!row) return { ok: false as const };
    // club_id in the WHERE clause, not just the request id: a valid token for
    // club A must not be able to flip a request belonging to club B.
    await DB.prepare(`UPDATE availability_requests SET status = ? WHERE id = ? AND club_id = ?`)
      .bind(data.status, data.id, row.id)
      .run();
    return { ok: true as const };
  });

export const setReservationStatus = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      token: z.string().min(16).max(64),
      id: z.number().int().positive(),
      status: z.enum(["pending", "confirmed", "declined"]),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const row = await clubByToken(DB, data.token);
    if (!row) return { ok: false as const };
    await DB.prepare(`UPDATE spot_reservations SET status = ? WHERE id = ? AND club_slug = ?`)
      .bind(data.status, data.id, row.slug)
      .run();
    return { ok: true as const };
  });

export const updateClubDetails = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      token: z.string().min(16).max(64),
      price_from: z.number().int().min(0).max(100000).nullable(),
      price_unit: z.string().max(120).nullable(),
      phone: z.string().max(60).nullable(),
      email: z.string().max(160).nullable(),
      website: z.string().max(300).nullable(),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const row = await clubByToken(DB, data.token);
    if (!row) return { ok: false as const };
    // A club editing its own page is the strongest price signal we can get,
    // so this also flips `verified` and refreshes the "last checked" date.
    await DB.prepare(
      `UPDATE clubs
          SET price_from = ?, price_unit = ?, phone = ?, email = ?, website = ?,
              verified = 1, researched_at = date('now')
        WHERE id = ?`,
    )
      .bind(
        data.price_from,
        data.price_unit,
        data.phone,
        data.email,
        data.website,
        row.id,
      )
      .run();
    return { ok: true as const };
  });

// ---------------------------------------------------------------------------
// Google Places sync.
//
// Uses the official Places API, never scraping. Google's terms allow storing
// place_id indefinitely but require other Place data to be refreshed (we treat
// 30 days as the ceiling) and displayed with attribution — which the club page
// does by labelling the number "Google reviews" beside our own rating.
//
// Needs GOOGLE_PLACES_API_KEY as a Worker secret. Without it this is a no-op,
// so the site behaves exactly as before until the key exists.
// ---------------------------------------------------------------------------

const PLACES_STALE_DAYS = 30;

async function findPlaceId(name: string, town: string, key: string): Promise<string | null> {
  const res = await fetch("https://places.googleapis.com/v1/places:searchText", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": key,
      "X-Goog-FieldMask": "places.id",
    },
    body: JSON.stringify({ textQuery: `${name}, ${town}`, maxResultCount: 1 }),
  });
  if (!res.ok) return null;
  const json = (await res.json()) as { places?: { id: string }[] };
  return json.places?.[0]?.id ?? null;
}

async function fetchPlaceRating(
  placeId: string,
  key: string,
): Promise<{ rating: number | null; count: number | null } | null> {
  const res = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
    headers: { "X-Goog-Api-Key": key, "X-Goog-FieldMask": "rating,userRatingCount" },
  });
  if (!res.ok) return null;
  const json = (await res.json()) as { rating?: number; userRatingCount?: number };
  return { rating: json.rating ?? null, count: json.userRatingCount ?? null };
}

export const syncGooglePlaces = createServerFn({ method: "POST" })
  .inputValidator(z.object({ password: z.string().min(1), limit: z.number().int().min(1).max(200).default(50) }))
  .handler(async ({ data }) => {
    const { DB, ADMIN_PASSWORD, GOOGLE_PLACES_API_KEY } = bindings() as {
      DB?: Db;
      ADMIN_PASSWORD?: string;
      GOOGLE_PLACES_API_KEY?: string;
    };
    if (!DB || !ADMIN_PASSWORD || data.password !== ADMIN_PASSWORD) {
      return { ok: false as const, reason: "auth" };
    }
    if (!GOOGLE_PLACES_API_KEY) {
      return { ok: false as const, reason: "no_key" };
    }

    // Oldest sync first, never-synced first of all.
    const { results } = await DB.prepare(
      `SELECT id, name, town, google_place_id, google_synced_at
         FROM clubs
        WHERE google_synced_at IS NULL
           OR julianday('now') - julianday(google_synced_at) > ?
        ORDER BY google_synced_at IS NOT NULL, google_synced_at ASC
        LIMIT ?`,
    )
      .bind(PLACES_STALE_DAYS, data.limit)
      .all<{
        id: number;
        name: string;
        town: string;
        google_place_id: string | null;
        google_synced_at: string | null;
      }>();

    let synced = 0;
    let missing = 0;
    for (const club of results ?? []) {
      const placeId = club.google_place_id ?? (await findPlaceId(club.name, club.town, GOOGLE_PLACES_API_KEY));
      if (!placeId) {
        missing++;
        // Stamp it so a club Google simply doesn't know isn't retried daily.
        await DB.prepare(`UPDATE clubs SET google_synced_at = datetime('now') WHERE id = ?`)
          .bind(club.id)
          .run();
        continue;
      }
      const rating = await fetchPlaceRating(placeId, GOOGLE_PLACES_API_KEY);
      await DB.prepare(
        `UPDATE clubs
            SET google_place_id = ?, google_rating = ?, google_review_count = ?,
                google_synced_at = datetime('now')
          WHERE id = ?`,
      )
        .bind(placeId, rating?.rating ?? null, rating?.count ?? null, club.id)
        .run();
      synced++;
    }
    return { ok: true as const, synced, missing, considered: (results ?? []).length };
  });

// ---------------------------------------------------------------------------
// Availability blocks: the club saying "not today" or "not this bed".
// ---------------------------------------------------------------------------

export type AvailabilityBlock = {
  id: number;
  scope: "day" | "spot";
  block_date: string;
  spot_id: string | null;
  note: string | null;
};

export const listAvailabilityBlocks = createServerFn({ method: "POST" })
  .inputValidator(z.object({ token: z.string().min(16).max(64) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const, blocks: [] as AvailabilityBlock[] };
    const row = await clubByToken(DB, data.token);
    if (!row) return { ok: false as const, blocks: [] as AvailabilityBlock[] };
    // Past closures are noise once the day has gone.
    const { results } = await DB.prepare(
      `SELECT id, scope, block_date, spot_id, note FROM availability_blocks
        WHERE club_slug = ? AND block_date >= date('now')
        ORDER BY block_date ASC, spot_id IS NULL DESC, spot_id ASC`,
    )
      .bind(row.slug)
      .all<AvailabilityBlock>();
    return { ok: true as const, blocks: results ?? [] };
  });

export const addAvailabilityBlock = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      token: z.string().min(16).max(64),
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      scope: z.enum(["day", "spot"]),
      spotId: z.string().min(2).max(6).nullable(),
      note: z.string().max(200).nullable(),
    }),
  )
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const row = await clubByToken(DB, data.token);
    if (!row) return { ok: false as const };
    if (data.scope === "spot" && !data.spotId) return { ok: false as const };
    await DB.prepare(
      `INSERT OR IGNORE INTO availability_blocks (club_id, club_slug, scope, block_date, spot_id, note)
       VALUES (?, ?, ?, ?, ?, ?)`,
    )
      .bind(
        row.id,
        row.slug,
        data.scope,
        data.date,
        data.scope === "spot" ? data.spotId : null,
        data.note,
      )
      .run();
    return { ok: true as const };
  });

export const removeAvailabilityBlock = createServerFn({ method: "POST" })
  .inputValidator(z.object({ token: z.string().min(16).max(64), id: z.number().int().positive() }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { ok: false as const };
    const row = await clubByToken(DB, data.token);
    if (!row) return { ok: false as const };
    // club_slug in the WHERE clause, not just the id: a valid token for club A
    // must not be able to unblock a date belonging to club B.
    await DB.prepare(`DELETE FROM availability_blocks WHERE id = ? AND club_slug = ?`)
      .bind(data.id, row.slug)
      .run();
    return { ok: true as const };
  });

// ---------------------------------------------------------------------------
// Editorial guides.
// ---------------------------------------------------------------------------

export type Post = {
  slug: string;
  lang: string;
  region_key: string | null;
  title: string;
  excerpt: string;
  body: string;
  club_slugs: string[];
  published_at: string;
  updated_at: string;
};

type PostRow = Omit<Post, "club_slugs"> & { club_slugs: string | null };

function parsePost(row: PostRow): Post {
  let club_slugs: string[] = [];
  try {
    const parsed = JSON.parse(row.club_slugs ?? "[]");
    if (Array.isArray(parsed)) club_slugs = parsed.filter((s) => typeof s === "string");
  } catch {
    club_slugs = [];
  }
  return { ...row, club_slugs };
}

// Three more clubs on the same coast, for the end of a club page. Ordered so a
// visitor who did not like this one still gets the strongest alternatives:
// clubs with a published price and a Google rating first.
// Counts for the owner-facing landing page. Real numbers, refreshed on every
// render: a partner page that overstates its own size is the fastest way to
// lose the first club that checks.
// Every coast we actually cover, grouped for the footer. Driven off the data so
// adding a region never means remembering to edit a hardcoded list, and a coast
// with a single club never becomes a dead-end link.
// Everything the homepage needs, and nothing else. It used to load the entire
// catalog and throw away all but three clubs per coast, which put a full copy
// of every club row into the HTML of the busiest page on the site.
// In-isolate memo's for the two queries every visitor triggers with an
// identical answer. On 3 sep 2026 the site went down on D1's free daily
// row-read limit: the footer's regionIndex ran a full-table GROUP BY on every
// single page render (5.8M rows/day on ~6k renders) and homeSummary re-ranked
// the catalogue on every homepage miss. The catalogue changes on imports, not
// by the minute; an hour of staleness per isolate is invisible, the read
// budget is not. Each isolate carries its own copy, which is exactly the
// granularity we want: a fresh deploy or recycled isolate rereads at most
// once an hour per colo.
const SUMMARY_MEMO_MS = 3_600_000;

// Coasts where the beach season runs opposite to, or independent of, the
// European summer. Used by homeSummary to reorder the homepage doorway during
// the winter half-year (October to April).
const WINTER_REGION_KEYS = new Set([
  "dubai",
  "abu-dhabi",
  "canaries",
  "cape-town",
  "bali",
  "phuket",
  "koh-samui",
  "miami",
  "tulum",
  "zanzibar",
  "mauritius",
]);
let homeSummaryMemo: { at: number; data: { total: number; regions: HomeRegion[] } } | null = null;
let regionIndexMemo: {
  at: number;
  data: { regions: { key: string; label: string; country: string; n: number }[] };
} | null = null;

export const homeSummary = createServerFn({ method: "GET" }).handler(async () => {
  const { DB } = bindings();
  if (!DB) return { total: 0, regions: [] as HomeRegion[] };
  if (homeSummaryMemo && Date.now() - homeSummaryMemo.at < SUMMARY_MEMO_MS) {
    return homeSummaryMemo.data;
  }

  const [totalRow, rows, regionPhotos] = await Promise.all([
    DB.prepare("SELECT COUNT(*) AS n FROM clubs WHERE hidden = 0").first<{ n: number }>(),
    // One pass: rank clubs inside their own coast by how much real data they
    // carry, then keep the top three. Coasts with fewer than three clubs are
    // dropped, so a "destination" never opens onto a lonely listing.
    DB.prepare(
      `WITH ranked AS (
         SELECT c.slug, c.name, c.town, c.region_key, c.region_label,
                c.price_from, c.price_unit, c.currency, c.image, c.image_credit,
                c.google_rating, c.google_review_count,
                (SELECT p.photo_name FROM place_photos p
                  WHERE p.club_slug = c.slug ORDER BY p.sort LIMIT 1) AS google_photo,
                COUNT(*) OVER (PARTITION BY c.region_key) AS region_count,
                ROW_NUMBER() OVER (
                  PARTITION BY c.region_key
                  ORDER BY (c.image IS NOT NULL) DESC, (c.price_from IS NOT NULL) DESC,
                           c.featured DESC, c.google_review_count DESC
                ) AS rn
         FROM clubs c WHERE c.hidden = 0
       )
       SELECT * FROM ranked WHERE rn <= 3 AND region_count >= 3
       ORDER BY region_count DESC, rn ASC`,
    ).all<HomeClubRow>(),
    // One small table (one row per coast), read once an hour per isolate with
    // the rest. The stored coast photo is what the destination card shows
    // first; without a row the card keeps borrowing its strongest club's.
    allRegionPhotos(DB),
  ]);

  // A homepage is a doorway, not the catalogue. Twenty coasts and sixty club
  // cards was a wall of 243 KB that nobody scrolls to the bottom of; eight
  // coasts gets someone to a region page, which is where the real choosing
  // happens.
  //
  // The doorway follows the season. From October to April the Mediterranean
  // coasts this list used to lead with are closed or cold, and the requests
  // dropped 85% in the two weeks the summer ended, while Dubai, the Canaries,
  // Cape Town and Southeast Asia are exactly in season. Winter-half visitors
  // get the coasts where a beach day is actually possible; the summer order
  // stays what it was. The memo above caps the staleness at an hour.
  const MAX_REGIONS = 8;
  const winterHalf = [9, 10, 11, 0, 1, 2, 3].includes(new Date().getUTCMonth());
  const sourceRows = rows.results ?? [];
  const orderedRows = winterHalf
    ? [...sourceRows].sort((a, b) => {
        const aw = WINTER_REGION_KEYS.has(a.region_key) ? 0 : 1;
        const bw = WINTER_REGION_KEYS.has(b.region_key) ? 0 : 1;
        return aw - bw || b.region_count - a.region_count || a.rn - b.rn;
      })
    : sourceRows;
  const byRegion = new Map<string, HomeRegion>();
  for (const r of orderedRows) {
    if (!byRegion.has(r.region_key) && byRegion.size >= MAX_REGIONS) continue;
    const region = byRegion.get(r.region_key) ?? {
      key: r.region_key,
      label: r.region_label,
      count: r.region_count,
      // A coast with no shot artwork of its own borrows the picture of its
      // strongest club, which is a real photograph of that actual coastline
      // rather than a stock beach that could be anywhere.
      // w=400, not 800: these render in ~400 px cards below the fold, and at
      // w=800 six of them (~800 KB) were downloading alongside the hero image
      // on a phone, which is what pushed the LCP past 4 s. 400 has to be one
      // of the proxy's ALLOWED_WIDTHS (api.place-photo.$.ts): any other value
      // silently falls back to 800, which is exactly the bug this fixes.
      // Same order as everywhere else: the club's own photo, then Google's.
      cover: coverSources(r, 400)[0]?.src ?? null,
      photo: regionPhotos.get(r.region_key) ?? null,
      clubs: [],
    };
    region.clubs.push(r);
    byRegion.set(r.region_key, region);
  }
  homeSummaryMemo = { at: Date.now(), data: { total: totalRow?.n ?? 0, regions: [...byRegion.values()] } };
  return homeSummaryMemo.data;
});

export type HomeClubRow = {
  slug: string;
  name: string;
  town: string;
  region_key: string;
  region_label: string;
  price_from: number | null;
  price_unit: string | null;
  currency: string;
  image: string | null;
  image_credit: string | null;
  google_rating: number | null;
  google_review_count: number | null;
  google_photo: string | null;
  region_count: number;
  rn: number;
};

export type HomeRegion = {
  key: string;
  label: string;
  count: number;
  cover: string | null;
  // The coast's own stored photo (region_photos: Pexels or Creative Commons),
  // which the card shows before any club's picture. Null until one is stored.
  photo: RegionPhoto | null;
  clubs: HomeClubRow[];
};

// ---------- Sunbed Price Index ----------
//
// The one dataset nobody else has: verified published prices across the
// catalogue. The honesty rules that make it citable: prices are grouped into
// comparable unit buckets (a EUR 25 single bed and a EUR 90 two-bed set must
// never be averaged together), the middle value is the median rather than the
// mean (one Belmond should not drag a whole coast up), a bucket is only shown
// with 4 or more data points, and AED stays AED with the conversion stated.
export type PriceBucket = "bed" | "set" | "daypass";
export type PriceIndexCountry = {
  country: string;
  currency: string;
  priced: number;
  buckets: Partial<Record<PriceBucket, { n: number; median: number; min: number; max: number }>>;
};
export type PriceIndexExtreme = {
  slug: string;
  name: string;
  town: string;
  country: string;
  price: number;
  currency: string;
  unit: string;
};
export type PriceIndexData = {
  totalClubs: number;
  pricedClubs: number;
  countries: PriceIndexCountry[];
  cheapest: PriceIndexExtreme[];
  dearest: PriceIndexExtreme[];
};

// Classifies a free-text price unit into a comparable bucket, or null when it
// is not comparable at all (weekly rates, half days, wakeboard sessions).
function priceBucket(unit: string | null): PriceBucket | null {
  const u = (unit ?? "per sunbed per day").toLowerCase();
  if (/(week|half day|morning|from 1[45]:00|from 19:00|per hour|session|minimum spend per person to sit)/.test(u)) return null;
  if (/(day pass|beach pass|day access|entrance|redeemable|admission)/.test(u)) return "daypass";
  if (/(2 beds|two beds|pair|two sunbeds|two loungers|two deck|2 loungers|parasol \+ 2|set of two|set \(two|for two per day|bed for 2|beds \+ parasol|1 umbrella \+ 2)/.test(u)) return "set";
  if (/(per sunbed|per bed|per lounger|per sun lounger|per mattress|per person|per adult|per ligbed|seabed|single bed|large sunbed|per day$)/.test(u)) return "bed";
  return null;
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
}

export const priceIndex = createServerFn({ method: "GET" }).handler(async (): Promise<PriceIndexData> => {
  const { DB } = bindings();
  if (!DB) return { totalClubs: 0, pricedClubs: 0, countries: [], cheapest: [], dearest: [] };
  const [{ results: rows }, total] = await Promise.all([
    DB.prepare(
      `SELECT slug, name, town, country, currency, price_from, price_unit
       FROM clubs WHERE hidden = 0 AND price_from IS NOT NULL`,
    ).all<{ slug: string; name: string; town: string; country: string; currency: string; price_from: number; price_unit: string | null }>(),
    DB.prepare(`SELECT COUNT(*) AS n FROM clubs WHERE hidden = 0`).first<{ n: number }>(),
  ]);

  const byCountry = new Map<string, { currency: string; priced: number; buckets: Map<PriceBucket, number[]> }>();
  const comparable: (PriceIndexExtreme & { bucket: PriceBucket })[] = [];
  for (const r of rows ?? []) {
    const entry = byCountry.get(r.country) ?? { currency: r.currency, priced: 0, buckets: new Map() };
    entry.priced += 1;
    const bucket = priceBucket(r.price_unit);
    if (bucket) {
      const list = entry.buckets.get(bucket) ?? [];
      list.push(r.price_from);
      entry.buckets.set(bucket, list);
      comparable.push({
        slug: r.slug, name: r.name, town: r.town, country: r.country,
        price: r.price_from, currency: r.currency, unit: r.price_unit ?? "per sunbed per day", bucket,
      });
    }
    byCountry.set(r.country, entry);
  }

  const countries: PriceIndexCountry[] = [...byCountry.entries()]
    .map(([country, e]) => ({
      country,
      currency: e.currency,
      priced: e.priced,
      buckets: Object.fromEntries(
        [...e.buckets.entries()]
          .filter(([, xs]) => xs.length >= 4)
          .map(([b, xs]) => [b, { n: xs.length, median: median(xs), min: Math.min(...xs), max: Math.max(...xs) }]),
      ),
    }))
    .filter((c) => Object.keys(c.buckets).length > 0)
    .sort((a, b) => b.priced - a.priced);

  // Extremes only make sense within one currency and one bucket: the single
  // sunbed is the unit everyone understands, so that is the ranking shown.
  const beds = comparable.filter((c) => c.bucket === "bed" && c.currency === "EUR");
  beds.sort((a, b) => a.price - b.price);
  return {
    totalClubs: total?.n ?? 0,
    pricedClubs: (rows ?? []).length,
    countries,
    cheapest: beds.slice(0, 8).map(({ bucket: _b, ...rest }) => rest),
    dearest: beds.slice(-8).reverse().map(({ bucket: _b, ...rest }) => rest),
  };
});

export const regionIndex = createServerFn({ method: "GET" }).handler(async () => {
  const { DB } = bindings();
  if (!DB) return { regions: [] as { key: string; label: string; country: string; n: number }[] };
  // The root loader calls this on EVERY page render; see SUMMARY_MEMO_MS.
  if (regionIndexMemo && Date.now() - regionIndexMemo.at < SUMMARY_MEMO_MS) {
    return regionIndexMemo.data;
  }
  const { results } = await DB.prepare(
    `SELECT region_key AS key, region_label AS label, country, COUNT(*) AS n
     FROM clubs WHERE hidden = 0 GROUP BY region_key HAVING n >= 3
     ORDER BY country ASC, n DESC`,
  ).all<{ key: string; label: string; country: string; n: number }>();
  regionIndexMemo = { at: Date.now(), data: { regions: results ?? [] } };
  return regionIndexMemo.data;
});

export const catalogSize = createServerFn({ method: "GET" }).handler(async () => {
  const { DB } = bindings();
  if (!DB) return { clubs: 0, countries: 0, regions: 0 };
  const row = await DB.prepare(
    `SELECT COUNT(*) AS clubs, COUNT(DISTINCT country) AS countries,
            COUNT(DISTINCT region_key) AS regions FROM clubs WHERE hidden = 0`,
  ).first<{ clubs: number; countries: number; regions: number }>();
  return row ?? { clubs: 0, countries: 0, regions: 0 };
});

export type RegionFacts = {
  region_key: string;
  region_label: string;
  country: string;
  clubs: number;
  towns: number;
  priced: number;
  currency: string;
  low: number | null;
  median: number | null;
  high: number | null;
  cheapest: string | null;
  cheapest_slug: string | null;
  rated: number;
  avg_rating: number | null;
  checked: string | null;
};

// The numbers behind a coast, computed rather than written.
//
// 147 clubs publish a rate and nobody else has collected them. "What does a
// sunbed cost on the Amalfi Coast" is a question people ask an answer engine
// and no page currently answers with a figure. This does, with the sample size
// attached: "EUR 10 to EUR 250 across the 12 clubs that publish a rate" is
// verifiable and honest, where "sunbeds cost EUR 72" would be neither.
//
// Median, not mean. Dubai's average of 209 is dragged up by a handful of
// AED 350 day passes and describes no actual club.
export const regionFacts = createServerFn({ method: "GET" })
  .inputValidator(z.object({ regionKey: z.string().min(1).max(60) }))
  .handler(async ({ data }): Promise<RegionFacts | null> => {
    const { DB } = bindings();
    if (!DB) return null;

    const base = await DB.prepare(
      `SELECT region_key, region_label, country,
              COUNT(*) AS clubs,
              COUNT(DISTINCT town) AS towns,
              SUM(price_from IS NOT NULL) AS priced,
              SUM(google_rating IS NOT NULL) AS rated,
              ROUND(AVG(google_rating), 1) AS avg_rating,
              MAX(researched_at) AS checked
         FROM clubs WHERE hidden = 0 AND region_key = ?`,
    )
      .bind(data.regionKey)
      .first<RegionFacts>();
    if (!base || !base.clubs) return null;

    // One currency per coast in practice, so take the one most rates are in
    // rather than mixing euros and dirhams into a meaningless range.
    const { results: prices } = await DB.prepare(
      `SELECT price_from, currency, name, slug FROM clubs
        WHERE hidden = 0 AND region_key = ? AND price_from IS NOT NULL
        ORDER BY price_from ASC`,
    )
      .bind(data.regionKey)
      .all<{ price_from: number; currency: string; name: string; slug: string }>();

    const rows = prices ?? [];
    if (rows.length === 0) {
      return { ...base, currency: "EUR", low: null, median: null, high: null,
               cheapest: null, cheapest_slug: null };
    }
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.currency, (counts.get(r.currency) ?? 0) + 1);
    const currency = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
    const inCurrency = rows.filter((r) => r.currency === currency);
    const mid = inCurrency[Math.floor(inCurrency.length / 2)];

    return {
      ...base,
      priced: inCurrency.length,
      currency,
      low: inCurrency[0].price_from,
      median: mid.price_from,
      high: inCurrency[inCurrency.length - 1].price_from,
      cheapest: inCurrency[0].name,
      cheapest_slug: inCurrency[0].slug,
    };
  });

export const relatedClubs = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string().min(1).max(80) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { clubs: [] as Club[] };
    // "Other clubs nearby" used to mean "anywhere in the same region", which on
    // a coast the length of Sicily put clubs 250 km away under that heading.
    // Order by actual proximity instead: squared degrees with the longitude
    // axis scaled by roughly cos(41 degrees), which is accurate enough to rank
    // neighbours across the Mediterranean and needs no trigonometry in SQLite.
    // Clubs without coordinates sort last rather than to the front.
    const { results } = await DB.prepare(
      `WITH origin AS (SELECT region_key, lat, lng FROM clubs WHERE slug = ?1)
       ${LIST_SELECT}
       WHERE c.hidden = 0
         AND c.region_key = (SELECT region_key FROM origin)
         AND c.slug <> ?1
       ORDER BY
         CASE
           WHEN c.lat IS NULL OR (SELECT lat FROM origin) IS NULL THEN 1e9
           ELSE (c.lat - (SELECT lat FROM origin)) * (c.lat - (SELECT lat FROM origin))
              + (c.lng - (SELECT lng FROM origin)) * (c.lng - (SELECT lng FROM origin)) * 0.57
         END ASC,
         (c.price_from IS NOT NULL) DESC, c.google_rating DESC, c.name ASC
       LIMIT 3`,
    )
      .bind(data.slug)
      .all<ClubRow>();
    return { clubs: (results ?? []).map(parseClub) };
  });

export type GalleryPhoto = {
  src: string;
  /**
   * Google requires the author's name on theirs. For our own photos this is
   * only filled when the licence asks for a credit; a club's own picture and a
   * Pexels or Unsplash photo carry none.
   */
  attribution: string;
  attributionUri: string | null;
  /** Licence name and link, shown next to the credit ("CC BY-SA 4.0"). */
  license: string | null;
  licenseUri: string | null;
  source: "club" | "licensed" | "google";
};

// The photo strip on a club page, in the one order the whole site uses:
//   1. photos we may keep: the club's own (uploaded, or supplied with
//      permission) and freely licensed ones, in their `sort` order;
//   2. Google Places photos, so a page is never bare, each with the author
//      attribution Google's terms require;
//   3. nothing, and the page draws the plate.
export const clubGallery = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string().min(1).max(80) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { photos: [] as GalleryPhoto[] };

    const [own, google] = await Promise.all([
      DB.prepare(
        `SELECT r2_key, source, credit_name, credit_url, license_name, license_url, credit_required
           FROM club_photos WHERE club_slug = ? AND status = 'approved'
          ORDER BY (source = 'licensed') ASC, sort ASC, created_at DESC LIMIT 12`,
      )
        .bind(data.slug)
        .all<{
          r2_key: string;
          source: string;
          credit_name: string | null;
          credit_url: string | null;
          license_name: string | null;
          license_url: string | null;
          credit_required: number;
        }>(),
      DB.prepare(
        `SELECT photo_name, attribution, attribution_uri FROM place_photos
         WHERE club_slug = ? ORDER BY sort LIMIT 8`,
      )
        .bind(data.slug)
        .all<{ photo_name: string; attribution: string; attribution_uri: string | null }>(),
    ]);

    const photos: GalleryPhoto[] = [
      ...(own.results ?? []).map((r) => {
        const credited = r.source === "licensed" && Boolean(r.credit_required) && Boolean(r.credit_name);
        return {
          src: `/api/photo/${r.r2_key}`,
          attribution: credited ? (r.credit_name ?? "") : "",
          attributionUri: credited ? r.credit_url : null,
          license: credited ? r.license_name : null,
          licenseUri: credited ? r.license_url : null,
          source: r.source === "licensed" ? ("licensed" as const) : ("club" as const),
        };
      }),
      ...(google.results ?? []).map((r) => ({
        src: `/api/place-photo/${r.photo_name}?w=800`,
        attribution: r.attribution,
        attributionUri: r.attribution_uri,
        license: null,
        licenseUri: null,
        source: "google" as const,
      })),
    ];
    return { photos };
  });

export type RegionPhoto = {
  src: string;
  // Pexels photos are stored at 800, 1600 and 2400 px wide, so a card can ask
  // for the small one and a wide banner for the large one; a CC hero has one
  // rendering and no srcSet.
  srcSet: string | null;
  width: number | null;
  height: number | null;
  // An honest description of what is in the picture, written when it was
  // chosen; null for the older CC rows, whose pages fall back to the label.
  alt: string | null;
  source: "pexels" | "wikimedia" | "openverse";
  credit: string | null;
  creditUrl: string | null;
  license: string | null;
  licenseUrl: string | null;
};

type RegionPhotoRow = {
  region_key?: string;
  r2_key: string;
  credit_name: string | null;
  credit_url: string | null;
  license_name: string | null;
  license_url: string | null;
  credit_required: number;
  // Migration 0058; undefined when the narrow (0057) select answered.
  source?: string | null;
  width?: number | null;
  height?: number | null;
  alt?: string | null;
};

const REGION_PHOTO_COLUMNS_0057 =
  "r2_key, credit_name, credit_url, license_name, license_url, credit_required";
const REGION_PHOTO_COLUMNS_0058 = `${REGION_PHOTO_COLUMNS_0057}, source, width, height, alt`;

// The three Pexels renderings sit side by side under one stem:
// _region/<key>/licensed/pexels-<id>-1600.webp is the stored key, and the
// 800 and 2400 siblings are named from it. A CC hero (fetch_licensed_photos.py)
// is one file, so it gets no srcSet.
const PEXELS_KEY_RE = /^(.*\/pexels-\d+)-1600\.webp$/;

function regionPhotoFromRow(r: RegionPhotoRow): RegionPhoto {
  const credited = Boolean(r.credit_required) && Boolean(r.credit_name);
  const m = PEXELS_KEY_RE.exec(r.r2_key);
  const source =
    r.source === "pexels" || r.source === "openverse" ? r.source : ("wikimedia" as const);
  return {
    src: `/api/photo/${r.r2_key}`,
    srcSet: m
      ? [800, 1600, 2400].map((w) => `/api/photo/${m[1]}-${w}.webp ${w}w`).join(", ")
      : null,
    width: r.width ?? null,
    height: r.height ?? null,
    alt: r.alt ?? null,
    source,
    credit: credited ? r.credit_name : null,
    creditUrl: credited ? r.credit_url : null,
    license: credited ? r.license_name : null,
    licenseUrl: credited ? r.license_url : null,
  };
}

// Every stored coast photo, keyed by region. The wide select needs migration
// 0058; when it fails (a deploy that beat the migration) the 0057 columns are
// read instead, and when even the table is missing the map is empty. Either
// way a page renders.
async function allRegionPhotos(DB: D1Database): Promise<Map<string, RegionPhoto>> {
  const out = new Map<string, RegionPhoto>();
  for (const cols of [REGION_PHOTO_COLUMNS_0058, REGION_PHOTO_COLUMNS_0057]) {
    try {
      const { results } = await DB.prepare(
        `SELECT region_key, ${cols} FROM region_photos`,
      ).all<RegionPhotoRow>();
      for (const r of results ?? []) if (r.region_key) out.set(r.region_key, regionPhotoFromRow(r));
      return out;
    } catch {
      // try the narrower shape
    }
  }
  return out;
}

// The licensed hero of a coast (migration 0057, filled by
// tools/fetch_licensed_photos.py). One row by primary key. The query is
// wrapped because the table is newer than the rest of the schema: a deploy
// that races the migration gets "no hero", not a broken region page.
export const regionPhoto = createServerFn({ method: "GET" })
  .inputValidator(z.object({ regionKey: z.string().min(1).max(60) }))
  .handler(async ({ data }): Promise<RegionPhoto | null> => {
    const { DB } = bindings();
    if (!DB) return null;
    // Wide select first (migration 0058), then the 0057 shape, then "no hero".
    for (const cols of [REGION_PHOTO_COLUMNS_0058, REGION_PHOTO_COLUMNS_0057]) {
      try {
        const r = await DB.prepare(`SELECT ${cols} FROM region_photos WHERE region_key = ?`)
          .bind(data.regionKey)
          .first<RegionPhotoRow>();
        return r ? regionPhotoFromRow(r) : null;
      } catch {
        // try the narrower shape
      }
    }
    return null;
  });

export type PhotoCreditRow = {
  kind: "club" | "region";
  key: string;
  name: string;
  town: string;
  region_key: string;
  region_label: string;
  credit_name: string | null;
  credit_url: string | null;
  license_name: string | null;
  license_url: string | null;
  credit_required: number;
};

// Every freely licensed photo on the site, for /photo-credits. CC BY and
// CC BY-SA require the author's name wherever the picture is shown, and the
// pages do that; this list is the one place that holds all of them, which is
// what a photographer looking for their own picture will find. CC0 photos
// need no credit and are listed all the same: it costs nothing to say where a
// picture came from.
export const photoCredits = createServerFn({ method: "GET" }).handler(async () => {
  const { DB } = bindings();
  if (!DB) return { rows: [] as PhotoCreditRow[] };
  const { results } = await DB.prepare(
    `SELECT p.club_slug AS key, c.name, c.town, c.region_key, c.region_label,
            p.credit_name, p.credit_url, p.license_name, p.license_url, p.credit_required
       FROM club_photos p JOIN clubs c ON c.slug = p.club_slug
      WHERE p.source = 'licensed' AND p.status = 'approved' AND c.hidden = 0
      ORDER BY c.region_label, c.name, p.sort`,
  ).all<Omit<PhotoCreditRow, "kind">>();
  const rows: PhotoCreditRow[] = (results ?? []).map((r) => ({ ...r, kind: "club" as const }));
  try {
    const regions = await DB.prepare(
      `SELECT r.region_key AS key, r.credit_name, r.credit_url, r.license_name, r.license_url, r.credit_required,
              (SELECT region_label FROM clubs WHERE region_key = r.region_key LIMIT 1) AS region_label
         FROM region_photos r ORDER BY region_label`,
      // No `source` column here on purpose: the list must still render on a
      // database that has 0057 but not 0058, and the licence name already
      // says which pipeline a row came from.
    ).all<{
      key: string;
      region_label: string | null;
      credit_name: string | null;
      credit_url: string | null;
      license_name: string | null;
      license_url: string | null;
      credit_required: number;
    }>();
    for (const r of regions.results ?? []) {
      rows.push({
        kind: "region",
        key: r.key,
        name: r.region_label ?? r.key,
        town: "",
        region_key: r.key,
        region_label: r.region_label ?? r.key,
        credit_name: r.credit_name,
        credit_url: r.credit_url,
        license_name: r.license_name,
        license_url: r.license_url,
        credit_required: r.credit_required,
      });
    }
  } catch {
    // region_photos not migrated yet: the club list still stands.
  }
  return { rows };
});

export const listPosts = createServerFn({ method: "GET" })
  .inputValidator(z.object({ lang: z.string().min(2).max(5) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { posts: [] as Post[] };
    // Fall back to English rather than showing an empty guides page in a
    // language we have not translated an article into yet.
    const { results } = await DB.prepare(
      `SELECT * FROM posts WHERE lang = ? ORDER BY published_at DESC, slug ASC`,
    )
      .bind(data.lang)
      .all<PostRow>();
    if (results?.length) return { posts: results.map(parsePost) };
    const fallback = await DB.prepare(
      `SELECT * FROM posts WHERE lang = 'en' ORDER BY published_at DESC, slug ASC`,
    ).all<PostRow>();
    return { posts: (fallback.results ?? []).map(parsePost) };
  });

export const getPost = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string().min(1).max(120), lang: z.string().min(2).max(5) }))
  .handler(async ({ data }) => {
    const { DB } = bindings();
    if (!DB) return { post: null as Post | null, clubs: [] as Club[] };
    let row = await DB.prepare(`SELECT * FROM posts WHERE slug = ? AND lang = ?`)
      .bind(data.slug, data.lang)
      .first<PostRow>();
    if (!row) {
      row = await DB.prepare(`SELECT * FROM posts WHERE slug = ? AND lang = 'en'`)
        .bind(data.slug)
        .first<PostRow>();
    }
    if (!row) return { post: null as Post | null, clubs: [] as Club[] };
    const post = parsePost(row);

    // The clubs the article ranks, so the page can show real cards under the
    // prose instead of leaving the reader to search for each one by hand.
    let clubs: Club[] = [];
    if (post.club_slugs.length) {
      const placeholders = post.club_slugs.map(() => "?").join(",");
      const { results } = await DB.prepare(
        `${LIST_SELECT} WHERE c.hidden = 0 AND c.slug IN (${placeholders})`,
      )
        .bind(...post.club_slugs)
        .all<ClubRow>();
      const bySlug = new Map((results ?? []).map((r) => [r.slug, parseClub(r)]));
      clubs = post.club_slugs.map((s) => bySlug.get(s)).filter((c): c is Club => Boolean(c));
    }
    return { post, clubs };
  });
